// The message format of the catalogs (docs/i18n.md), shared by the page (public/i18n.js), the worker (src/i18n.ts) and
// the check (tools/i18n-check.mjs). No DOM, no imports. ICU-lite:
//   {name}                                   a value as text
//   {n, number}                              a number in the language's style (Intl.NumberFormat)
//   {n, plural, =0 {none} one {# item} other {# items}}   # inside is n, formatted; =N matches exactly first
//   {kind, select, agent {Agent} other {Person}}         by value (none: other); `other` is required
//   '{'  '}'  ''                             a literal brace, a literal apostrophe (a lone ' is just an apostrophe)
// Unversioned on purpose (like icons.js): it's imported by URL from several modules, which must share one instance.

/** A parsed message: a list of parts, each a string or {arg, type?, options?}. Throws on bad syntax. */
export function parse(msg) {
  let i = 0;
  const n = msg.length;
  function text(inPlural) {
    const parts = [];
    let s = '';
    while (i < n) {
      const c = msg[i];
      if (c === "'") {
        const d = msg[i + 1];
        if (d === "'") { s += "'"; i += 2; continue; }
        if (d === '{' || d === '}' || (inPlural && d === '#')) { // quoted until the next lone '
          i++;
          while (i < n && !(msg[i] === "'" && msg[i + 1] !== "'")) { if (msg[i] === "'") i++; s += msg[i++]; }
          i++; continue;
        }
        s += c; i++; continue;
      }
      if (c === '{') { if (s) parts.push(s); s = ''; i++; parts.push(arg()); continue; }
      if (c === '}') break;
      if (c === '#' && inPlural) { if (s) parts.push(s); s = ''; i++; parts.push({ hash: true }); continue; }
      s += c; i++;
    }
    if (s) parts.push(s);
    return parts;
  }
  const ws = () => { while (i < n && /\s/.test(msg[i])) i++; };
  function word() { ws(); const m = /^[A-Za-z0-9_=-]+/.exec(msg.slice(i)); if (!m) throw Error(`expected a name at ${i}`); i += m[0].length; ws(); return m[0]; }
  function arg() {
    const name = word();
    if (msg[i] === '}') { i++; return { arg: name }; }
    if (msg[i] !== ',') throw Error(`expected , or } after {${name} at ${i}`);
    i++;
    const type = word();
    if (type === 'number') { if (msg[i] !== '}') throw Error(`expected } after {${name}, number at ${i}`); i++; return { arg: name, type }; }
    if (type !== 'plural' && type !== 'select') throw Error(`unknown type "${type}" in {${name}, …}`);
    if (msg[i] !== ',') throw Error(`expected , after {${name}, ${type} at ${i}`);
    i++;
    const options = {};
    for (;;) {
      ws();
      if (msg[i] === '}') { i++; break; }
      const key = word();
      if (msg[i] !== '{') throw Error(`expected { after "${key}" in {${name}, ${type}}`);
      i++;
      options[key] = text(type === 'plural');
      if (msg[i] !== '}') throw Error(`unclosed option "${key}" in {${name}, ${type}}`);
      i++;
    }
    if (!options.other) throw Error(`{${name}, ${type}} needs an "other" option`);
    return { arg: name, type, options };
  }
  const parts = text(false);
  if (i < n) throw Error(`unmatched } at ${i}`);
  return parts;
}

/** The argument names a parsed message uses (for the check: every language must use the same ones). */
export function argNames(parts, out = new Set()) {
  for (const p of parts) if (typeof p === 'object' && p.arg) {
    out.add(p.arg);
    if (p.options) for (const o of Object.values(p.options)) argNames(o, out);
  }
  return [...out];
}

const cache = new Map();
const plurals = new Map(), numbers = new Map();
const pluralOf = (lang) => { let p = plurals.get(lang); if (!p) plurals.set(lang, (p = new Intl.PluralRules(lang))); return p; };
const numberOf = (lang) => { let f = numbers.get(lang); if (!f) numbers.set(lang, (f = new Intl.NumberFormat(lang))); return f; };

/** msg with vars filled in, for lang (a BCP 47 tag: plural rules and number style). A missing var shows as {name}. */
export function format(msg, vars, lang) {
  let parts = cache.get(msg);
  if (!parts) { try { parts = parse(msg); } catch { parts = [msg]; } cache.set(msg, parts); }
  return render(parts, vars ?? {}, lang, null);
}
function render(parts, vars, lang, hash) {
  let s = '';
  for (const p of parts) {
    if (typeof p === 'string') { s += p; continue; }
    if (p.hash) { s += hash === null ? '#' : numberOf(lang).format(hash); continue; }
    const v = vars[p.arg];
    if ((v === undefined || v === null) && p.type === 'select') { s += render(p.options.other, vars, lang, hash); continue; } // no value: other
    if (v === undefined || v === null) { s += `{${p.arg}}`; continue; }
    if (p.type === 'number') s += numberOf(lang).format(Number(v));
    else if (p.type === 'plural') {
      const x = Number(v), o = p.options;
      s += render(o[`=${x}`] ?? o[pluralOf(lang).select(x)] ?? o.other, vars, lang, x);
    } else if (p.type === 'select') s += render(p.options[String(v)] ?? p.options.other, vars, lang, hash);
    else s += String(v);
  }
  return s;
}
